import { Injectable, Type } from '@angular/core';
import { AbcCore, CompareHelper } from '@barthauer/abc/core';
import { ProfileInfo, ProfileService } from '@barthauer/abc/core/authentication';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import {
  WizardExportConfig,
  WizardExportContentPage,
  WizardExportService,
} from '@barthauer/abc/interfaces/core/wizards/wizard-export';
import { iif, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { M150ExportQueryModel, M150WizardSettingDocument, M150WizardTransferObject } from './models/export.model';
import { SettingSelectionPageComponent } from './pages/setting-selection-page/setting-selection-page.component';
import { AbcWindowComponent } from '@barthauer/abc/ui-components';
import { ExportModeSelectionPageComponent } from './pages/export-mode-selection-page/export-mode-selection-page.component';
import { WorkerResult } from 'libs/abc/core/data/src/lib/models/worker.model';

@Injectable({
  providedIn: 'root',
})
export class M150ExportService {
  private m150ExporWorkerUri: string;
  public windowInstance?: AbcWindowComponent;
  public filterPolygon: number[][];

  constructor(
    configService: AbcConfigService,
    private profileService: ProfileService,
    private abcCore: AbcCore,
    private wizardExportService: WizardExportService
  ) {
    this.m150ExporWorkerUri = configService.configuration.WebApi.ServiceURLs.ExtensionWorkersStartM150Export;
  }

  public loadWizard(filterPolygon: number[][], windowInstance?: AbcWindowComponent): void {
    this.windowInstance = windowInstance;
    this.filterPolygon = filterPolygon;
    const config: WizardExportConfig = new WizardExportConfig();
    config.transferObject = new M150WizardTransferObject();
    config.exportFileNameSchema = 'DWAM150Export_' + WizardExportConfig.TimeStamp_Placeholder + '.xml';
    config.welcomePage.title = 'DWA-M 150 Export-Assistent';
    config.welcomePage.description =
      'Dieser Assistent bietet Ihnen die Möglichkeit, die in Ihren BaSYS-Daten enthaltenen Zustandserfassungen von Entwässerungssystemen in eine DWA-M 150 XML-Datei zu exportieren.';

    config.contentPages.push(
      this.createWizardContentPage(
        'Konfiguration',
        'Bitte wählen Sie eine Konfiguration aus.',
        SettingSelectionPageComponent
      )
    );

    config.contentPages.push(
      this.createWizardContentPage(
        'Kartenausschnitt',
        'Der Export kann entweder über den sichtbaren Bereich oder einen markierten Bereich erfolgen.',
        ExportModeSelectionPageComponent
      )
    );

    config.startExportWorker = (transferObject) =>
      this.startM150ExportWorker((<M150WizardTransferObject>transferObject).settingDocument.id, this.filterPolygon);

    this.wizardExportService.loadWizard(config);
  }

  public minimizeWizardWindow(): void {
    this.windowInstance?.minimizeDialog();
  }

  public maximizeWizardWindow(): void {
    this.windowInstance?.maximizeDialog();
  }

  private createWizardContentPage(
    title: string,
    description: string,
    component: Type<unknown>
  ): WizardExportContentPage {
    const page: WizardExportContentPage = new WizardExportContentPage();
    page.title = title;
    page.description = description;
    page.component = component;

    return page;
  }

  public getWizardSettingDocuments(): Observable<M150WizardSettingDocument[]> {
    return this.profileService
      .getCurrentProfile(true)
      .pipe(
        mergeMap((profileInfo) =>
          iif(() => profileInfo.settings, this.readWizardSettingsFromProfile(profileInfo), of([]))
        )
      );
  }
  private readWizardSettingsFromProfile(profileInfo: ProfileInfo): Observable<M150WizardSettingDocument[]> {
    const settings: M150WizardSettingDocument[] = new Array<M150WizardSettingDocument>();
    if (profileInfo.settings?.Commands && profileInfo.settings?.Commands.length > 0) {
      const command = profileInfo.settings.Commands.find((c: any) => c.name === 'Export_DWAM150');
      if (command && command.ConfigurationGroups && command.ConfigurationGroups.length > 0) {
        const groupSettings = command.ConfigurationGroups.find((c: any) => c.name === 'Settings');
        const documents = groupSettings.Documents;
        if (documents && documents.length > 0) {
          for (const d of documents) {
            settings.push(new M150WizardSettingDocument(d));
          }
        }
      }
    }
    return of(settings);
  }

  private startM150ExportWorker(bceDocumentId: string, filterPolygon: number[][]): Observable<WorkerResult> {
    const query: M150ExportQueryModel = new M150ExportQueryModel();
    query.BCEDocumentId = bceDocumentId;
    query.ExteriorPolygon = filterPolygon;

    return this.abcCore.data.executeWebApiPOSTMethod(this.m150ExporWorkerUri, query, true).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any) : null)),
      map((data) => new WorkerResult(data))
    );
  }
}
